<template>
  <div class="bg-gray-50 px-6 py-2" v-if="show">
    <label for="transport_type" class="block text-sm font-medium text-gray-700">Transport type</label>
    <div class="relative" @click="toggleDropdown">
      <input id="transport_type" type="text" readonly :value="selectedValue ? selectedValue.display : ''"
        @blur="transportTypeTouched = true"
        class="cursor-pointer block w-full rounded-md bg-white px-3 py-2 text-gray-900 shadow-sm focus:border-secondary focus:ring focus:ring-secondary focus:ring-opacity-50 font-medium"
        role="combobox" aria-haspopup="listbox" :aria-expanded="dropdownOpen.toString()" placeholder="Vælg transporttype">
      <button type="button" class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
        <!-- Arrow icon -->
        <svg class="h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <ul v-show="dropdownOpen"
        class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
        id="options" role="listbox">
        <li v-for="(option, index) in options" :key="index" class="relative cursor-default select-none py-2 pl-8 pr-4"
          :class="{ 'text-white bg-secondary': selectedValue === option, 'text-gray-900': selectedValue !== option }"
          @click.stop="selectValue(option)" role="option">
          <span class="block truncate">{{ option.display }}</span>
          <span v-if="selectedValue === option" class="absolute inset-y-0 left-0 flex items-center pl-1.5">
            <!-- Checkmark icon -->
            <svg class="h-5 w-5 text-secondary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
              stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </span>
        </li>
      </ul>
    </div>
    <!-- Error message -->
    <p v-if="!selectedValue && transportTypeTouched" class="mt-2 text-sm text-red-600">Vælg venligst en transporttype</p>
  </div>
</template>

<script lang="ts">
import { TransportType } from '@/models/report.model'
import { defineComponent, ref, watch } from 'vue'
import { useStore } from 'vuex'

export default defineComponent({
  name: 'TransportTypeSelect',
  setup () {
    const store = useStore()
    const options = ref([
      {
        display: 'Firma bil',
        value: TransportType.CompanyCar
      },
      {
        display: 'Egen bil',
        value: TransportType.PrivateCar
      },
      {
        display: 'Passager i bil',
        value: TransportType.Passenger
      },
      {
        display: 'Tog',
        value: TransportType.Train
      },
      {
        display: 'Fly',
        value: TransportType.Plain
      },
      {
        display: 'Helikopter',
        value: TransportType.Helicopter
      }
    ])
    const transportTypeTouched = ref(false)
    const selectedValue = ref<{ display: string, value: TransportType } | null>(null)
    const dropdownOpen = ref(false)
    const show = ref(false)

    function toggleDropdown () {
      dropdownOpen.value = !dropdownOpen.value
    }

    function selectValue (option: { display: string, value: TransportType }) {
      selectedValue.value = option
      dropdownOpen.value = false
    }

    watch(selectedValue, (newValue) => {
      const valueToCommit = newValue != null ? newValue.value : undefined
      store.commit('report/UPDATE_REPORT', { key: 'transportType', value: valueToCommit })
    })

    watch(() => store.state.report.currentReport, (currentReport) => {
      show.value = currentReport && currentReport.state !== 'FREE'
      if (currentReport && currentReport.transportType) {
        const matchingOption = options.value.find(option => option.value === currentReport.transportType)
        if (matchingOption) {
          selectedValue.value = matchingOption
        } else {
          selectedValue.value = null
        }
      }
    }, { immediate: true })

    return {
      options,
      selectedValue,
      dropdownOpen,
      toggleDropdown,
      selectValue,
      transportTypeTouched,
      show
    }
  }
})
</script>
